import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isVerifiedBusiness, partnerAngreskjemaUrl } from '../../util/userTypes';
import { PARTNER_ENTRANCE } from '../../util/partnerEntrance';

import ExternalLink from '../ExternalLink/ExternalLink';
import NamedLink from '../NamedLink/NamedLink';

import { BADGE_Z_PATH, BADGE_WORD_PATH, Z_ALONE_OFFSET_Y } from './partnerBadgePaths';
import css from './PartnerMarking.module.css';

/**
 * Where a buyer finds the withdrawal forms when a partner has none of its own
 * yet: the page listing every partner's form.
 */
export const WITHDRAWAL_FORMS_PAGE = '/angreskjema';

/**
 * The small sage mark beside "Zuprize Partner" on the profile page.
 */
const PartnerMark = ({ className }) => (
  <svg className={classNames(css.mark, className)} viewBox="0 0 100 100" aria-hidden="true">
    <circle cx="50" cy="50" r="50" className={css.markDisc} />
    <text x="50" y="70" textAnchor="middle" className={css.markLetter}>
      Z
    </text>
  </svg>
);

/**
 * Guro's badge, drawn from her SVG: the white disc and the Z, with the word
 * PARTNER under it unless `withWord` is false (the Z then moves to the centre).
 */
const BadgeArt = ({ withWord, className }) => (
  <svg className={className} viewBox="100 101 1050 1050" aria-hidden="true" focusable="false">
    <circle cx="624.6" cy="625.8" r="521.4" className={css.badgeDisc} />
    <path
      d={BADGE_Z_PATH}
      className={css.badgeInk}
      transform={withWord ? undefined : `translate(0 ${Z_ALONE_OFFSET_Y})`}
    />
    {withWord ? <path d={BADGE_WORD_PATH} className={css.badgeInk} fillRule="evenodd" /> : null}
  </svg>
);

/**
 * The Z mark on a verified partner's listing photo: top-left on cards and on
 * the listing page's photos. Renders nothing for other sellers. The caller
 * positions it.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.user - the seller as a user entity
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
export const PartnerPhotoMark = props => {
  const { user, className } = props;
  const intl = useIntl();
  if (!isVerifiedBusiness(user)) {
    return null;
  }
  const label = intl.formatMessage({ id: 'PartnerMarking.partner' });
  return (
    <span
      className={classNames(css.photoMark, className)}
      role="img"
      aria-label={label}
      title={label}
    >
      <BadgeArt withWord={false} className={css.badgeArt} />
    </span>
  );
};

/**
 * Guro's full badge (with PARTNER) on its own, as a picture: for places that
 * are about being a partner rather than about one seller, such as the
 * business sign-up page. Decorative: the text beside it says the same.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export const PartnerBadge = ({ className }) => (
  <span className={classNames(css.badge, className)} aria-hidden="true">
    <BadgeArt withWord className={css.badgeArt} />
  </span>
);

/**
 * Guro's full badge (with PARTNER) beside a verified partner's name, linking
 * to the Butikker page. Renders nothing for other sellers.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.user - the seller as a user entity
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
export const PartnerBadgeLink = props => {
  const { user, className } = props;
  const intl = useIntl();
  if (!isVerifiedBusiness(user)) {
    return null;
  }
  const label = intl.formatMessage({ id: 'PartnerMarking.partner' });
  return (
    <NamedLink
      className={classNames(css.badgeLink, className)}
      name={PARTNER_ENTRANCE.routeName}
      aria-label={label}
      title={label}
    >
      <BadgeArt withWord className={css.badgeArt} />
    </NamedLink>
  );
};

/**
 * "Zuprize Partner" under a verified business seller's own name on their
 * profile page. Renders nothing for anyone else.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.user - the seller as a user entity
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
export const PartnerLine = props => {
  const { user, className } = props;
  if (!isVerifiedBusiness(user)) {
    return null;
  }
  return (
    <p className={classNames(css.partnerLine, className)}>
      <PartnerMark className={css.lineMark} />
      <FormattedMessage id="PartnerMarking.partner" />
    </p>
  );
};

/**
 * What the buyer can expect after buying: one compact line with a partner's
 * 14-day right of withdrawal and a link to the form, or a private seller's one
 * neutral line. Shown on the listing page and at checkout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.seller - the seller as a user entity
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
export const WithdrawalNotice = props => {
  const { seller, className } = props;
  if (!seller?.id) {
    return null;
  }
  if (!isVerifiedBusiness(seller)) {
    return (
      <p className={classNames(css.privateNote, className)}>
        <FormattedMessage id="PartnerMarking.privateSeller" />
      </p>
    );
  }
  return (
    <div className={classNames(css.rights, className)}>
      <p className={css.rightsTitle}>
        <FormattedMessage id="PartnerMarking.withdrawalTitle" />
      </p>
      {/* The seller's own form, in a new tab so the buyer keeps the listing or
          checkout; the page of all partners' forms while it has none. */}
      <ExternalLink
        className={css.rightsLink}
        href={partnerAngreskjemaUrl(seller) || WITHDRAWAL_FORMS_PAGE}
      >
        <FormattedMessage id="PartnerMarking.withdrawalForm" />
      </ExternalLink>
    </div>
  );
};

export default PartnerLine;
